import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { mapRow, withPropertyColumns, type Property } from "./useProperties";
import { useTenant } from "@/hooks/useTenant";

/**
 * Returns the most-viewed available properties, ranked by detail-page visits.
 * Uses the get_top_viewed_properties database function so view counts are
 * aggregated server-side (homepage impressions are NOT counted, only
 * actual visits to the property detail page).
 *
 * Falls back to the most recently created listings if there are not enough
 * properties with view data yet.
 */
export function useMostViewedProperties(limit = 4) {
  const tenant = useTenant();
  return useQuery({
    queryKey: ["most-viewed-properties", limit, tenant.id],
    queryFn: async () => {
      // This market's views from the last 60 days; the database used to
      // count every view ever, in every market, on each home page visit.
      const { data: topRows, error: topError } = await (supabase.rpc as any)(
        "get_top_viewed_properties",
        { _limit: limit, _tenant: tenant.id, _days: 60 },
      );
      if (topError) throw topError;

      const topIds: string[] = (topRows || []).map((r: any) => r.property_id);

      // Fetch the actual properties, both the viewed ones and a fallback set
      // of recent listings to fill the slots if needed. Scoped to the
      // current tenant via tenant_id so cross-tenant properties never leak
      // in even if they happen to be in the top-viewed list globally.
      // Bounded fetches instead of loading the whole table to pick a few:
      // the ranked rows (by id) + a small newest fallback. Same tenant / QC
      // scoping and column set as the grid.
      const base = (columns: string) =>
        supabase
          .from("properties")
          .select(columns)
          .eq("status", "available")
          // Quality-control gate (no-op until a tenant enables QC).
          .eq("quality_visible", true)
          .eq("tenant_id", tenant.id);

      const [rankedRes, newestRes] = await Promise.all([
        withPropertyColumns((cols) => base(cols).in("id", topIds)),
        withPropertyColumns((cols) => base(cols).order("created_at", { ascending: false }).limit(limit)),
      ]);
      if (rankedRes.error) throw rankedRes.error;
      if (newestRes.error) throw newestRes.error;

      const rankedById = new Map<string, Property>();
      for (const row of rankedRes.data || []) {
        const p = mapRow(row);
        rankedById.set(p.id, p);
      }

      const final: Property[] = [];
      // Ranked first, honoring the RPC's view-count order.
      for (const id of topIds) {
        if (final.length >= limit) break;
        const p = rankedById.get(id);
        if (p && !final.some((r) => r.id === p.id)) final.push(p);
      }
      // Fill remaining slots with the newest listings not already included.
      for (const row of newestRes.data || []) {
        if (final.length >= limit) break;
        const p = mapRow(row);
        if (!final.some((r) => r.id === p.id)) final.push(p);
      }

      // Attach owner profile data
      const ownerIds = [...new Set(final.map((p) => p.owner_id).filter(Boolean))] as string[];
      if (ownerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, display_name, avatar_url")
          .in("user_id", ownerIds);
        const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) ?? []);
        for (const prop of final) {
          if (prop.owner_id) {
            const profile = profileMap.get(prop.owner_id);
            prop.ownerName = profile?.display_name ?? undefined;
            prop.ownerAvatar = profile?.avatar_url ?? undefined;
          }
        }
      }

      return final;
    },
  });
}
